import React, {
  FC, useEffect, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import cx from 'clsx';
import { Icon } from '@mdi/react';
import { mdiInformationOutline } from '@mdi/js';
import {
  ButtonArea, Chip, ModalTitle, useDraggableOverlay,
} from 'components';
import { useLocale } from 'lib';
import { BuildingContact } from './useBuildingContact';
import styles from './infoModal.module.scss';

export type ContactOption = { contact: BuildingContact; distanceM: number | null };

type InfoModalProps = {
  options: ContactOption[];
  selected: BuildingContact;
  onSelect: (contact: BuildingContact) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Explains what an automatically detected building is, before the operator contacts it.
 * Rendered in the body: the map is its own stacking context, so a modal inside it would stay below the page overlays.
 */
export const InfoModal: FC<InfoModalProps> = ({
  options, selected, onSelect, onConfirm, onCancel,
}) => {

  const { t } = useLocale();
  const scrimRef = useRef<HTMLDivElement>(null);
  // the scrim covers the viewport, so that is where the modal can be dragged
  const draggableProps = useDraggableOverlay({ containerRef: scrimRef, handleSelector: '[data-drag-handle]' });
  useEffect(() => {

    const onKeyDown = (event: KeyboardEvent) => {

      if (event.key === 'Escape') onCancel();

    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);

  }, [onCancel]);

  return createPortal(
    // mouse down, not click: a click that ends on the scrim after a drag must not close the modal
    <div ref={scrimRef} className={styles.scrim} onMouseDown={(event) => event.target === event.currentTarget && onCancel()}>
      <div
        {...draggableProps}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
      >
        <ModalTitle dragHandle text={t('Alarm_Detail_Map.BuildingContact.Title')} onClose={onCancel}/>
        <div className={styles.body}>
          <p className={styles.intro}>{t('Alarm_Detail_Map.BuildingContact.Modal.Intro')}</p>
          <div className={styles.notice}>
            <Icon path={mdiInformationOutline} size="20px"/>
            <div>
              <div className={styles.noticeTitle}>{t('Alarm_Detail_Map.BuildingContact.Modal.NoticeTitle')}</div>
              <div className={styles.noticeBody}>{t('Alarm_Detail_Map.BuildingContact.Modal.NoticeBody')}</div>
            </div>
          </div>
          <div className={styles.choose}>{t('Alarm_Detail_Map.BuildingContact.Modal.Choose')}</div>
          <ul className={styles.options} role="radiogroup">
            {options.map(({ contact, distanceM }) => {

              const checked = contact.name === selected.name && contact.phoneNumber === selected.phoneNumber;

              return (
                <li key={`${contact.name}-${contact.phoneNumber}`}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    className={cx(styles.option, checked && styles.checked)}
                    onClick={() => onSelect(contact)}
                  >
                    <span className={styles.radio}/>
                    <span className={styles.optionInfo}>
                      <span className={styles.optionName}>{contact.name}</span>
                      {contact.address && <span className={styles.optionAddress}>{contact.address}</span>}
                    </span>
                    {distanceM !== null && <span className={styles.distance}>{`${distanceM} m`}</span>}
                  </button>
                </li>
              );

            })}
          </ul>
        </div>
        <ButtonArea>
          <Chip
            variant="primary"
            size="large"
            label={t('Alarm_Detail_Map.BuildingContact.Modal.Confirm')}
            onClick={onConfirm}
          />
          <Chip
            variant="light100"
            size="large"
            label={t('Alarm_Detail_Map.BuildingContact.Modal.Cancel')}
            onClick={onCancel}
          />
        </ButtonArea>
      </div>
    </div>,
    document.body,
  );

};
